import { Routes, Route } from 'react-router-dom';
import { ROUTES, ROLES, PUBLIC_ROUTES } from '@/constants';
import { ROUTE_PERMISSIONS } from '@/constants/permissions';
import PublicLayout from '@/layouts/PublicLayout/PublicLayout';
import AdminLayout from '@/layouts/AdminLayout/AdminLayout';
import AuthLayout from '@/layouts/AuthLayout/AuthLayout';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import RoleRoute from './RoleRoute';
import { publicWebsiteRoutes, portalRoutes, authRoutes, adminRoutes, scorerRoutes, NotFoundPage, PublicNotFoundPage } from './routeConfig';

import PermissionDeniedPage from '@/pages/PermissionDenied/PermissionDeniedPage';

const getRoutePermission = (path) => {
  if (path === ROUTES.DASHBOARD) return ROUTE_PERMISSIONS.dashboard;
  if (path.startsWith('/tournaments')) return ROUTE_PERMISSIONS.tournaments;
  if (path.startsWith('/teams')) return ROUTE_PERMISSIONS.teams;
  if (path.startsWith('/players')) return ROUTE_PERMISSIONS.players;
  if (path.startsWith('/matches')) return ROUTE_PERMISSIONS.matches;
  if (path.startsWith('/grounds')) return ROUTE_PERMISSIONS.grounds;
  if (path === ROUTES.REPORTS) return ROUTE_PERMISSIONS.reports;
  if (path === ROUTES.NOTIFICATIONS) return ROUTE_PERMISSIONS.notifications;
  if (path.startsWith('/gallery')) return ROUTE_PERMISSIONS.gallery;
  if (path.startsWith('/sponsors')) return ROUTE_PERMISSIONS.sponsors;
  if (path === ROUTES.SETTINGS) return ROUTE_PERMISSIONS.settings;
  if (path === ROUTES.LIVE_SCORE) return ROUTE_PERMISSIONS.matches;
  return null;
};

const getSettingsChildPermission = (childPath) => {
  if (childPath === 'users') return ROUTE_PERMISSIONS.settingsUsers;
  if (childPath === 'roles') return null;
  return ROUTE_PERMISSIONS.settings;
};

function AppRouter() {
  const settingsRoute = adminRoutes.find((route) => route.path === ROUTES.SETTINGS);

  return (
    <Routes>
      {portalRoutes.map(({ path, Component }) => (
        <Route key={path} path={path} Component={Component} />
      ))}

      <Route element={<PublicLayout />}>
        {publicWebsiteRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} Component={Component} />
        ))}
      </Route>

      <Route element={<PublicRoute />}>
        <Route element={<AuthLayout />}>
          {authRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} Component={Component} />
          ))}
        </Route>
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route path={ROUTES.PERMISSION_DENIED} Component={PermissionDeniedPage} />

        <Route element={<RoleRoute permission={ROUTE_PERMISSIONS.scorer} />}>
          {scorerRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} Component={Component} />
          ))}
        </Route>

        <Route element={<AdminLayout />}>
          <Route element={<RoleRoute anyRead />}>
            {adminRoutes
              .filter((route) => route.path !== ROUTES.SETTINGS)
              .map(({ path, Component, children }) => {
                const permission = getRoutePermission(path);

                if (children?.length) {
                  return (
                    <Route key={path} element={<RoleRoute permission={permission} />}>
                      <Route path={path} Component={Component}>
                        {children.map(({ path: childPath, Component: ChildComponent }) => (
                          <Route key={childPath} path={childPath} Component={ChildComponent} />
                        ))}
                      </Route>
                    </Route>
                  );
                }

                return (
                  <Route key={path} element={<RoleRoute permission={permission} />}>
                    <Route path={path} Component={Component} />
                  </Route>
                );
              })}

            {settingsRoute && (
              <Route element={<RoleRoute permission={ROUTE_PERMISSIONS.settings} />}>
                <Route path={settingsRoute.path} Component={settingsRoute.Component}>
                  {settingsRoute.children?.map(({ path: childPath, Component: ChildComponent }) => {
                    if (childPath === 'roles') {
                      return (
                        <Route key={childPath} element={<RoleRoute roles={[ROLES.SUPER_ADMIN]} />}>
                          <Route path={childPath} Component={ChildComponent} />
                        </Route>
                      );
                    }

                    return (
                      <Route
                        key={childPath}
                        element={<RoleRoute permission={getSettingsChildPermission(childPath)} />}
                      >
                        <Route path={childPath} Component={ChildComponent} />
                      </Route>
                    );
                  })}
                </Route>
              </Route>
            )}
          </Route>
        </Route>
      </Route>

      <Route path={PUBLIC_ROUTES.NOT_FOUND} Component={PublicNotFoundPage} />
      <Route path={ROUTES.NOT_FOUND} Component={NotFoundPage} />
    </Routes>
  );
}

export default AppRouter;
